import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { orderBy, sumBy } from 'lodash'

import { AnalyticsValue } from './utils/types'
import { AnalyticsField, DuckDBSchemaType, FilterField } from '@types'

import { ANALYTICS_CAPTIONS, ANALYTICS_DATE_STORAGE_KEY } from '../utils/const'
import { ASSET_FIELD } from '@/const'

import { getCurrentDate } from '@/helpers/dates'
import { getFilterTags, getFilterValues } from '../utils/helpers'

import useAnalyticsStore from '../index'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useRepoLocalStorage } from '@/hooks/repoLocalStorage'
import { useTagsBunchStore } from '@/store/tags/bunch'

import { DuckDBService } from '@/services/duckdb'

export const TABLE_NAME = 'analytics_value'

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  [AnalyticsField.DATE]: 'DATE',
  [AnalyticsField.ACCOUNT_ID]: 'VARCHAR',
  [AnalyticsField.ASSET_ID]: 'VARCHAR',
  tags: 'VARCHAR[]',
  [AnalyticsField.AMOUNT]: 'DOUBLE',
  [AnalyticsField.POSITION_CLOSE]: 'DOUBLE',
  [AnalyticsField.POSITION_COST]: 'DOUBLE',
  [AnalyticsField.POSITION_GAIN]: 'DOUBLE',
  [AnalyticsField.D_POSITION_CLOSE]: 'DOUBLE',
  [AnalyticsField.D_POSITION_GAIN]: 'DOUBLE',
}

export default defineStore('analytics-value', () => {
  // INIT
  const analyticsStore = useAnalyticsStore()

  const duckdbInstance = DuckDBService.instance()

  const list = ref<Record<string, AnalyticsValue[]>>()

  const loading = ref(false)
  const deprecated = ref<boolean>()
  const trigger = ref(0)

  const storageDate = useRepoLocalStorage<string | undefined>(
    ANALYTICS_DATE_STORAGE_KEY,
    undefined,
  )

  // GETTERS

  const isInit = computed(() => deprecated.value !== undefined)

  const getList = computed(() => {
    if (analyticsStore.getError) return {}
    return list.value
  })

  const getDatesRange = computed(() => {
    return list.value && Object.keys(list.value)
  })

  const getValues = computed(() => {
    if (!storageDate.value) return []
    return getList.value?.[storageDate.value] || []
  })

  const getNetWorth = computed(() => {
    if (!getValues.value?.length) return 0
    return sumBy(getValues.value, AnalyticsField.POSITION_CLOSE)
  })

  const previousDate = computed(() => {
    if (!storageDate.value) return
    const dateIndex = getDatesRange.value?.indexOf(storageDate.value)
    if (!dateIndex || dateIndex < 1) return
    return getDatesRange.value?.[dateIndex - 1]
  })

  const previousDateSum = computed(() => {
    if (!previousDate.value) return 0
    const scope = getList.value?.[previousDate.value]
    if (!scope) return 0
    return sumBy(scope, AnalyticsField.POSITION_CLOSE)
  })

  const getChangeTotal = computed(
    () => getNetWorth.value - previousDateSum.value,
  )

  const getChangeValue = computed(() => {
    if (!previousDateSum.value) return
    const result = getChangeTotal.value / previousDateSum.value
    return Math.round(result * 10000) / 100
  })

  // SETTERS

  const setList = async (data: {
    value_view: Record<string, AnalyticsValue[]>;
  }) => {
    if (deprecated.value === false) return
    if (!data.value_view) return
    list.value = data.value_view
    deprecated.value = false
    await duckdbInstance.createTable(TABLE_NAME, TABLE_SCHEMA)
    await Promise.all(
      Object.values(data.value_view ?? {}).map(values =>
        duckdbInstance.pushData(TABLE_NAME, values, TABLE_SCHEMA),
      ),
    )
    trigger.value++
  }

  // ACTIONS

  const clear = () => {
    list.value = undefined
    loading.value = false
    deprecated.value = undefined
    trigger.value = 0
  }

  return {
    isLoading: loading,
    isDeprecated: deprecated,

    isInit,
    trigger,

    getList,
    getDatesRange,

    getValues,
    getNetWorth,
    getChangeTotal,
    getChangeValue,

    setList,

    clear,
  }
})

export const useAnalyticsValueFilters = () => {
  const analyticsStore = useAnalyticsStore()
  const assetsBunchStore = useAssetsBunchStore()
  const tagsBunchStore = useTagsBunchStore()

  const analyticsDate = computed(
    () => analyticsStore.getDate || getCurrentDate({}),
  )

  const getValues = getFilterValues<AnalyticsValue>(
    TABLE_NAME,
    analyticsDate,
    assetsBunchStore,
  )
  const getTags = getFilterTags(TABLE_NAME, analyticsDate, tagsBunchStore)

  const filters = computed(() => {
    const result: FilterField[] = [
      {
        name: AnalyticsField.ACCOUNT_ID,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT_ID],
        getter: () => getValues(AnalyticsField.ACCOUNT_ID, ASSET_FIELD.NAME),
      },
      {
        name: AnalyticsField.ACCOUNT_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT_TYPE],
        getter: () => getValues(AnalyticsField.ACCOUNT_ID, ASSET_FIELD.TYPE),
      },
      {
        name: AnalyticsField.ASSET_ID,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_ID],
        getter: () => getValues(AnalyticsField.ASSET_ID, ASSET_FIELD.NAME),
      },
      {
        name: AnalyticsField.ASSET_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_TYPE],
        getter: () => getValues(AnalyticsField.ASSET_ID, ASSET_FIELD.TYPE),
      },
    ]
    tagsBunchStore.getMetricTags?.forEach(name => {
      result.push({
        name,
        caption: name,
        getter: () => getTags(name),
      })
    })
    return orderBy(result, 'caption')
  })

  return {
    filters,
  }
}
